'use client'

import { useEffect, useMemo, useState } from 'react'
import { useTranslations } from 'next-intl'
import { cx } from 'class-variance-authority'
import { PlusCircleIcon } from '@phosphor-icons/react/dist/ssr/PlusCircle'
import { CaretUpDownIcon } from '@phosphor-icons/react/dist/ssr/CaretUpDown'
import { Avatar, Button, ScrollArea } from '@skop-app/lib-front'
import { UserInput, UserTeam } from '@skop-app/lib-domain/types'
import { matchLabel } from '@skop-app/lib-domain/utils'
import { DynamicIcon } from '@/components/atoms/DynamicIcon'

import { Link } from '@/navigation'
import { Command, CommandGroup, CommandItem } from '@/components/atoms/Command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/atoms/Popover'
import { useSelectedTeam } from '@/features/team-selection'
import { pref } from '@/utils/pref'
import { useUser } from '@/services/user/user.query'
import {
  getPersonalWorkspaceMembership,
  getSelectableTeams,
  isPersonalWorkspaceMembership,
  resolveActiveTeamId,
} from '@/features/team-selection/lib/resolveActiveTeamId'
import { useUserContext } from '@/context'

type TeamSelectorProps = {
  navbarCollapsed: boolean
}

export function TeamSelector({ navbarCollapsed }: TeamSelectorProps) {
  const t = useTranslations()
  const { data: userData, isLoading } = useUser()
  const { isClient } = useUserContext()

  const user = userData?.user
  const preferredTeamId = pref(user?.preferences, 'user-default-team-id')
  const teams = userData?.organizations
  const selectableTeams = useMemo(() => getSelectableTeams(teams), [teams])
  const personalWorkspace = useMemo(
    () => getPersonalWorkspaceMembership(teams),
    [teams],
  )

  const { selectedTeamId, setSelectedTeam, setSolo, clear, raw } =
    useSelectedTeam()

  const [open, setOpen] = useState(false)

  // Initialize cookie from available teams or clear invalid cookie
  useEffect(() => {
    if (isLoading || (!personalWorkspace && selectableTeams.length === 0))
      return

    // If a cookie exists but doesn't match any known team, clear it
    if (
      selectedTeamId &&
      ![...selectableTeams, personalWorkspace].some(
        (team) => team?.value === selectedTeamId,
      )
    ) {
      void clear()
      return
    }
  }, [clear, isLoading, personalWorkspace, selectableTeams, selectedTeamId])

  const switchTeam = async (organizationId: string) => {
    if (
      organizationId === user?.id ||
      organizationId === personalWorkspace?.value
    ) {
      await setSolo()
    } else {
      await setSelectedTeam(organizationId)
    }
    setOpen(false)
  }

  // Resolve active team id centrally
  const resolvedTeamId = resolveActiveTeamId(teams, raw, preferredTeamId)

  const currentTeam = selectableTeams.find(
    (team) => team.value === resolvedTeamId,
  )
  const isSoloSelected = resolvedTeamId === null
  const currentWorkspace = isSoloSelected ? personalWorkspace : undefined

  // Show loading state or prevent hydration mismatch
  if (!isClient || isLoading || !userData) {
    return (
      <div className="flex w-full items-center gap-4 px-2 py-1">
        <div className="flex size-10 flex-none animate-pulse items-center justify-center rounded-full bg-slate-600" />
        {!navbarCollapsed && (
          <div className="flex w-full items-center justify-between">
            <div className="w-40">
              <div className="h-3 w-3/4 animate-pulse rounded bg-slate-600" />
              <div className="mt-2 h-2 w-1/2 animate-pulse rounded bg-slate-600" />
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          aria-expanded={open}
          aria-haspopup="listbox"
          className="w-full justify-between !p-0"
        >
          {currentTeam || currentWorkspace ? (
            <TeamMenuItem
              team={currentTeam ?? currentWorkspace}
              user={user}
              navbarCollapsed={navbarCollapsed}
              className="text-white"
              selected
              main
            />
          ) : (
            <TeamMenuItem
              user={user}
              navbarCollapsed={navbarCollapsed}
              className="text-white"
              selected
              main
            />
          )}

          {!navbarCollapsed && (
            <div className="flex size-10 flex-none items-center justify-center rounded-full hover:bg-slate-800">
              <CaretUpDownIcon className="size-5 text-gray-100 opacity-75" />
            </div>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="rounded-sm bg-white !p-0 mt-2">
        <Command
          value={
            currentTeam?.value ?? currentWorkspace?.value ?? user?.id ?? ''
          }
        >
          <ScrollArea>
            <CommandGroup className="max-h-72 !p-0 !py-1">
              {personalWorkspace ? (
                <CommandItem
                  value={personalWorkspace.value}
                  onSelect={switchTeam}
                  className="cursor-pointer rounded-sm bg-white hover:!bg-primary-500"
                >
                  <TeamMenuItem
                    team={personalWorkspace}
                    user={user}
                    navbarCollapsed={false}
                    selected={isSoloSelected}
                  />
                </CommandItem>
              ) : (
                <CommandItem
                  value={user?.id}
                  onSelect={switchTeam}
                  className="cursor-pointer rounded-sm bg-white hover:!bg-primary-500"
                >
                  <SoloItem user={user} navbarCollapsed={false} />
                </CommandItem>
              )}
              {selectableTeams.map((org) => (
                <CommandItem
                  key={`${org.value}-${org.id}`}
                  value={org.value}
                  onSelect={switchTeam}
                  className="cursor-pointer rounded-sm bg-white hover:!bg-primary-500"
                >
                  <TeamMenuItem
                    team={org}
                    user={user}
                    navbarCollapsed={false}
                    selected={org.value === resolvedTeamId}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </ScrollArea>
          <CommandGroup className="border-t border-t-zinc-100 bg-zinc-50 !p-0 !py-1">
            <CommandItem>
              <Link
                href="/team"
                onClick={() => setOpen(false)}
                className="flex items-center gap-2 underline-offset-2 hover:underline"
              >
                <PlusCircleIcon className="size-4" />
                <span>{t('Navigation.create-team')}</span>
              </Link>
            </CommandItem>
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export function TeamMenuItem({
  team,
  user,
  navbarCollapsed,
  className,
  selected,
  main,
}: {
  team?: UserTeam
  user?: UserInput
  navbarCollapsed: boolean
  className?: string
  selected: boolean
  main?: boolean
}) {
  const t = useTranslations('Navigation')

  if (!team) {
    return (
      <div className="w-full">
        <SoloItem
          user={user}
          navbarCollapsed={navbarCollapsed}
          className="cursor-pointer text-white"
        />
      </div>
    )
  }
  const roleLabel = isPersonalWorkspaceMembership(team)
    ? t('solo-user')
    : matchLabel(team.role || '', 'role')?.label
  const teamStatus = matchLabel(team.status || '', 'status')

  return (
    <div className={cx('flex w-full gap-4', className)}>
      <Avatar
        name={team.label}
        color={team.color}
        picture={team.logo}
        variant="square"
        size="lg"
      />
      {!navbarCollapsed && (
        <div className="flex w-full items-center justify-between">
          <div className="text-left">
            <span className="line-clamp-1">{team.label}</span>
            <span className="block font-light">{roleLabel}</span>
          </div>
          {!main && (
            <span title={teamStatus.label} className="shrink-0">
              <DynamicIcon
                name={teamStatus.icon || 'Question'}
                className={cx({
                  'size-5 text-gray-500': true,
                  'text-green-500': selected,
                  'text-slate-300': !selected,
                })}
              />
            </span>
          )}
        </div>
      )}
    </div>
  )
}

const SoloItem = ({
  user,
  navbarCollapsed,
  className,
}: {
  user?: UserInput
  navbarCollapsed: boolean
  className?: string
}) => {
  const t = useTranslations('Navigation')
  if (!user) return null

  return (
    <div className={cx('flex w-full gap-4', className)}>
      <Avatar name={user?.name} variant="round" size="lg" />
      {!navbarCollapsed && (
        <div className="flex w-full items-center justify-between">
          <div className="text-left">
            <span className="line-clamp-1">{user?.name}</span>
            <span className="block font-light">{t('solo-user')}</span>
          </div>
        </div>
      )}
    </div>
  )
}
